import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import SubmissionForm from "@/components/SubmissionForm";
import { Helmet } from "react-helmet-async";
import { CONTACT } from "@/data/site";
import { Mail, Phone, ArrowUpRight, MapPin, Clock } from "lucide-react";

const MAPS_QUERY = encodeURIComponent(`${CONTACT.address} ${CONTACT.building}`);
const MAPS_LINK = `https://www.google.com/maps/search/?api=1&query=${MAPS_QUERY}`;
const MAPS_EMBED = `https://www.google.com/maps?q=${MAPS_QUERY}&output=embed`;

const LOCAL_BUSINESS_JSONLD = {
  "@context": "https://schema.org",
  "@type": "EventPlanner",
  name: CONTACT.brand,
  legalName: CONTACT.legalName,
  image: "https://desir-events.com/og.jpg",
  telephone: CONTACT.phoneFixe,
  email: CONTACT.email,
  address: {
    "@type": "PostalAddress",
    streetAddress: "129 rue de Servient",
    addressLocality: "Lyon",
    postalCode: "69003",
    addressCountry: "FR",
  },
  areaServed: ["Lyon", "Paris", "Valence", "France"],
  openingHours: "Mo-Su 00:00-24:00",
  url: "https://desir-events.com",
};

export default function Contact() {
  return (
    <>
      <Seo title="Contact" description="Contactez Désir Events (DESIREVENT 2.0), agence événementielle B2B à Lyon, Paris et Valence. Contact téléphonique possible 7j/7 — étude de votre demande sous 24 h ouvrées." path="/contact" />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(LOCAL_BUSINESS_JSONLD)}</script>
      </Helmet>
      <PageHero eyebrow="Contact" title={<>Écrivons la suite<br /><span className="italic text-lime-dim">ensemble.</span></>} />

      <section className="mx-auto max-w-[1440px] px-5 py-16 md:px-10 md:py-24">
        <div className="grid gap-16 md:grid-cols-12">
          <div className="md:col-span-4">
            <Reveal>
              <span className="text-[11px] uppercase tracking-[0.2em] text-ink/40">Coordonnées</span>
              <div className="mt-6 space-y-5">
                <p className="flex items-start gap-3 text-ink/80"><MapPin size={18} className="mt-0.5 shrink-0 text-lime-dim" /> <span>{CONTACT.address}<br /><span className="text-ink/50">{CONTACT.building}</span></span></p>
                <a href={`tel:${CONTACT.phoneFixe.replace(/\s/g, "")}`} className="flex items-center gap-3 text-lg text-ink/80 hover:text-lime-dim" data-testid="contact-phone"><Phone size={18} className="text-lime-dim" /> {CONTACT.phoneFixe}</a>
                <a href={`tel:${CONTACT.phoneMobile.replace(/\s/g, "")}`} className="flex items-center gap-3 text-lg text-ink/80 hover:text-lime-dim" data-testid="contact-phone-mobile"><Phone size={18} className="text-lime-dim" /> {CONTACT.phoneMobile}</a>
                <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-3 text-lg text-ink/80 hover:text-lime-dim" data-testid="contact-email"><Mail size={18} className="text-lime-dim" /> {CONTACT.email}</a>
                <p className="flex items-center gap-3 text-ink/70"><Clock size={18} className="text-lime-dim" /> {CONTACT.availability}</p>
              </div>
              <p className="mt-6 rounded-xl bg-mist p-4 text-sm text-ink/60">
                <span className="font-medium text-ink">{CONTACT.zones}</span>
              </p>
            </Reveal>
            <Reveal delay={1}>
              <a href={CONTACT.hostessSite} target="_blank" rel="noopener noreferrer" className="group mt-8 block rounded-2xl border border-ink/10 bg-mist p-6 transition-colors duration-500 hover:border-lime-dim/50">
                <span className="text-[11px] uppercase tracking-[0.2em] text-lime-dim">Site partenaire</span>
                <p className="mt-2 font-serif text-xl text-ink">Hôtes & hôtesses d'accueil</p>
                <span className="mt-2 flex items-center gap-1 text-sm text-ink/60 group-hover:text-lime-dim">hotesses-events.fr <ArrowUpRight size={14} /></span>
              </a>
            </Reveal>
          </div>
          <div className="md:col-span-7 md:col-start-6">
            <div className="rounded-2xl border border-ink/10 bg-mist p-8 md:p-10">
              <SubmissionForm
                type="contact"
                testid="contact-form"
                submitLabel="Envoyer le message"
                fields={[
                  { name: "name", label: "Nom complet", required: true, placeholder: "Jean Dupont" },
                  { name: "company", label: "Société", placeholder: "Votre entreprise" },
                  { name: "email", label: "Email", type: "email", required: true, placeholder: "jean@entreprise.com" },
                  { name: "phone", label: "Téléphone", placeholder: "06 12 34 56 78" },
                  { name: "message", label: "Votre message", type: "textarea", full: true, required: true, placeholder: "Comment pouvons-nous vous aider ?" },
                ]}
              />
            </div>
          </div>
        </div>

        {/* Google Maps — SEO local Lyon */}
        <Reveal>
          <div className="mt-16 overflow-hidden rounded-2xl border border-ink/10" data-testid="contact-map">
            <div className="flex flex-col items-start justify-between gap-3 bg-mist px-6 py-4 md:flex-row md:items-center">
              <p className="flex items-center gap-2 text-sm text-ink/70"><MapPin size={16} className="text-lime-dim" /> {CONTACT.address} — {CONTACT.building}</p>
              <a href={MAPS_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm font-medium text-ink hover:text-lime-dim" data-testid="google-maps-link">Voir sur Google Maps <ArrowUpRight size={15} /></a>
            </div>
            <iframe
              title="Localisation Désir Events — Lyon"
              src={MAPS_EMBED}
              width="100%"
              height="380"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
        </Reveal>
      </section>
    </>
  );
}
